import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useWebSocket } from '../../context/WebSocketContext';
import LogoutButton from '../auth/LogoutButton';
import SearchBar from '../reusable/SearchBar';
import LoginLink from '../auth/LoginLink';
import SignUpLink from '../auth/SignUpLink';
import { buildDisplayName } from '../../utils/nameFormatting';
import '../../styles/components/layout/NavBar.css';

const PUBLIC_PROFILE_API = 'https://0zr99zyhbk.execute-api.eu-west-2.amazonaws.com/dev';

export default function NavBar() {
  const { isAuthenticated, userAttributes, userProfileSlug } = useAuth();
  const { openChats } = useWebSocket();
  const location = useLocation();
  const accountRef = useRef(null);
  const triggerRef = useRef(null);
  const firstLinkRef = useRef(null);
  const [showAccount, setShowAccount] = useState(false);
  const [photo, setPhoto] = useState(null);
  const userSub = userAttributes?.sub;
  const displayName = buildDisplayName(userAttributes?.given_name, userAttributes?.family_name) || 'Your account';
  const initial = displayName.charAt(0).toUpperCase();
  const homeLabel = userAttributes?.['custom:userRole'] === 'photographer' ? 'Dashboard' : 'Home';
  const photoUrl = photo?.userSub === userSub && photo?.slug === userProfileSlug ? photo.url : '';
  const unreadCount = (openChats || []).reduce((total, chat) => total + (chat.unreadCount || 0), 0);

  useEffect(() => {
    setShowAccount(false);
  }, [location.key, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !userSub || !userProfileSlug) return;
    const controller = new AbortController();

    const loadPhoto = async () => {
      try {
        const response = await fetch(`${PUBLIC_PROFILE_API}/profiles/${encodeURIComponent(userProfileSlug)}`, {
          signal: controller.signal,
        });
        if (!response.ok) return;
        const profile = await response.json();
        if (!controller.signal.aborted && profile.id === userSub) {
          setPhoto({ userSub, slug: userProfileSlug, url: profile.profile_picture_s3_url || '' });
        }
      } catch {
        // The account controls remain usable with an initial if the photo is unavailable.
      }
    };

    loadPhoto();
    return () => controller.abort();
  }, [isAuthenticated, userSub, userProfileSlug]);

  useEffect(() => {
    if (!showAccount) return;
    firstLinkRef.current?.focus();

    const handleOutsideClick = (event) => {
      if (!accountRef.current?.contains(event.target)) setShowAccount(false);
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setShowAccount(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showAccount]);

  const closeAccount = () => setShowAccount(false);

  return (
    <nav className="navbar" aria-label="Main navigation">
      <div className="nav-left">
        <NavLink to={isAuthenticated ? '/home' : '/'} className="brand">ViewFinders</NavLink>
      </div>

      <div className="nav-core">
        <NavLink to="/" end className="nav-browse">Find photographers</NavLink>
        <div className="search-wrap" aria-label="site search">
          <SearchBar />
        </div>
      </div>

      <div className="nav-right">
        {isAuthenticated ? (
          <div
            className="account-navigation"
            ref={accountRef}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) closeAccount();
            }}
          >
            <button
              type="button"
              className="account-trigger"
              ref={triggerRef}
              aria-label={`Account navigation for ${displayName}`}
              aria-expanded={showAccount}
              aria-controls="account-navigation-panel"
              onClick={() => setShowAccount((value) => !value)}
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown') {
                  event.preventDefault();
                  setShowAccount(true);
                  firstLinkRef.current?.focus();
                }
              }}
            >
              {photoUrl ? <img src={photoUrl} alt="" onError={() => setPhoto(null)} /> : <span aria-hidden="true">{initial}</span>}
            </button>

            {showAccount && (
              <div id="account-navigation-panel" className="account-dropdown" role="group" aria-label="Your account">
                <div className="account-dropdown__header">{displayName}</div>
                <NavLink to="/home" ref={firstLinkRef} onClick={closeAccount}>{homeLabel}</NavLink>
                {userProfileSlug ? (
                  <NavLink to={`/profile/${encodeURIComponent(userProfileSlug)}`} onClick={closeAccount}>Your profile</NavLink>
                ) : (
                  <button type="button" className="account-dropdown__unavailable" disabled>Your profile</button>
                )}
                <NavLink to="/messages" onClick={closeAccount}>
                  Messages
                  {unreadCount > 0 && <span className="messages-unread-badge" aria-label={`${unreadCount} unread messages`}>{unreadCount > 99 ? '99+' : unreadCount}</span>}
                </NavLink>
                <div className="account-dropdown__footer" onClick={closeAccount}>
                  <LogoutButton className="account-dropdown__logout" />
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            <LoginLink size="sm" />
            <SignUpLink variant="secondary" size="sm">Sign up</SignUpLink>
          </>
        )}
      </div>
    </nav>
  );
}
